import React, { useMemo, useState } from "react";

import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import { Box, Input, InputAdornment } from "@mui/material";
import { format } from "date-fns";
import { observer } from "mobx-react-lite";

import calendarIcon from "@/assets/icons/calendar.png";
import {
  sortByClientNameAsc,
  sortByClientNameDesc,
  sortByPinnedPrimarily,
} from "@/components/Sidebar/ClickComponents/Client/utils/sort";
import { Spacer } from "@/components/Spacer/Spacer";
import {
  LargeTitle,
  LargeTitleBold,
  Title3Bold,
} from "@/components/Typography";
import { ClientsPageClientFragment } from "@/gql/graphql";
import useSearchText from "@/hooks/useSearchText";
import { useVisibilityState } from "@/hooks/useVisibilityState";

import { ClientTable } from "./components/ClientTable";
import TimelineDrawer from "./components/ClientTimelineActivityDrawer";
import { clientsQuery } from "./graphql/clientsPageQueries";

const ClientsPage: React.FC = observer(() => {
  const timelineDrawer = useVisibilityState();
  const [searchKey, setSearchKey] = useSearchText({ debounce: 16 });
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedClientViewActivity, setSelectedClientViewActivity] = useState<
    ClientsPageClientFragment | undefined
  >(undefined);

  // API
  const { data, loading } = useQuery(clientsQuery);

  const clients = useMemo(() => {
    if (!data?.clients.nodes) return [];

    const filteredClients = data.clients.nodes.filter((client) => {
      const search = searchKey;
      return client.profile?.fullName.includes(search);
    });

    const sortedClients = [...filteredClients].sort(
      sortOrder === "asc"
        ? sortByPinnedPrimarily(sortByClientNameAsc)
        : sortByPinnedPrimarily(sortByClientNameDesc)
    );

    return sortedClients;
  }, [data?.clients.nodes, searchKey, sortOrder]);

  // Functions
  const handleSortToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };

  return (
    <Box p={{ xs: 2.5, md: 5 }}>
      <Box display="flex" alignItems="center" flexWrap="wrap" rowGap={1}>
        <Box
          component="img"
          src={calendarIcon}
          alt=""
          sx={{ height: { xs: 36, md: 56 }, mr: { xs: 2, md: 3 } }}
        />
        <Box display="flex" alignItems="baseline" flexWrap="wrap" columnGap={1}>
          <LargeTitle
            text="Today is"
            sx={{ fontSize: { xs: 22, md: "33px" } }}
          />
          <LargeTitleBold
            text={format(new Date(), "MMMM d, yyyy")}
            sx={{ fontSize: { xs: 22, md: "33px" } }}
          />
        </Box>
      </Box>
      <Spacer h={40} />
      <Box
        display="flex"
        sx={{
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Title3Bold text={`All Clients (${clients.length})`} />
        <Box sx={{ width: { xs: "100%", md: "300px" } }}>
          <Input
            fullWidth
            placeholder="Search Client"
            startAdornment={
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            }
            onChange={(evt) => setSearchKey(evt.target.value)}
          />
        </Box>
      </Box>
      <Spacer h={32} />
      <ClientTable
        clients={clients}
        isLoading={loading}
        sortOrder={sortOrder}
        onSortToggle={handleSortToggle}
        onViewClientActivity={(client: ClientsPageClientFragment) => {
          timelineDrawer.show();
          setSelectedClientViewActivity(client);
        }}
      />
      {selectedClientViewActivity ? (
        <TimelineDrawer
          client={selectedClientViewActivity}
          isOpen={timelineDrawer.isVisible}
          onClose={timelineDrawer.hide}
        />
      ) : (
        <></>
      )}
    </Box>
  );
});

export default ClientsPage;
